import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import type { NotificationDto, WidgetConfig } from './types';
import { useWidgetAuth } from './WidgetAuthService';

type NotificationApiDto = NotificationDto & {
    isUnread?: boolean;
};

interface WidgetNotificationsContextValue {
    notifications: NotificationDto[];
    unreadCount: number;
    isConnected: boolean;
    isLoading: boolean;
    latestNotification: NotificationDto | null;
    clearLatestNotification: () => void;
    markAsRead: (notificationId: number) => Promise<void>;
}

const WidgetNotificationsContext = createContext<WidgetNotificationsContextValue | null>(null);

export function useWidgetNotifications() {
    const context = useContext(WidgetNotificationsContext);
    return context;
}

function normalizeNotification(notification: NotificationApiDto): NotificationDto {
    return {
        ...notification,
        unread: notification.unread ?? notification.isUnread ?? false,
    };
}

interface WidgetNotificationsProviderProps {
    config: WidgetConfig;
    children: React.ReactNode;
}

/**
 * Notification service provider for the widget
 * Handles SignalR connection and notification state
 */
export const WidgetNotificationsProvider: React.FC<WidgetNotificationsProviderProps> = ({
    config,
    children,
}) => {
    const { token, isAuthenticated } = useWidgetAuth();
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [latestNotification, setLatestNotification] = useState<NotificationDto | null>(null);
    const connectionRef = useRef<signalR.HubConnection | null>(null);

    const clearLatestNotification = useCallback(() => {
        setLatestNotification(null);
    }, []);

    // Calculate unread count
    const unreadCount = useMemo(() => {
        return notifications.filter(n => n.unread).length;
    }, [notifications]);

    // Fetch existing notifications
    const fetchNotifications = useCallback(async () => {
        if (!token) return;

        try {
            setIsLoading(true);
            const response = await fetch(`${config.apiUrl}/notifications`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const data: NotificationApiDto[] = await response.json();
                setNotifications(data.map(normalizeNotification));
            } else {
                console.error('[Atlas Widget] Failed to fetch notifications:', response.statusText);
            }
        } catch (error) {
            console.error('[Atlas Widget] Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, [config.apiUrl, token]);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId: number) => {
        if (!token) return;

        try {
            const response = await fetch(
                `${config.apiUrl}/notifications/mark-read?notificationId=${notificationId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n => (n.id === notificationId ? { ...n, unread: false } : n))
                );
            } else {
                console.error('[Atlas Widget] Failed to mark notification as read:', response.statusText);
            }
        } catch (error) {
            console.error('[Atlas Widget] Failed to mark notification as read:', error);
        }
    }, [config.apiUrl, token]);

    // Set up SignalR connection
    useEffect(() => {
        if (!isAuthenticated || !token) {
            return;
        }

        let stopped = false;

        async function startConnection() {
            // Build hub URL from API URL
            let hubUrl = config.apiUrl.replace(/\/$/, '');
            if (hubUrl.endsWith('/api')) {
                hubUrl = hubUrl.slice(0, -4);
            }

            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`${hubUrl}/hubs/notifications`, {
                    accessTokenFactory: async () => token!,
                    transport: signalR.HttpTransportType.WebSockets,
                    withCredentials: true,
                })
                .withAutomaticReconnect()
                .build();

            // Handle new notification
            connection.on('notificationAdded', (payload: NotificationApiDto) => {
                const notification = normalizeNotification(payload);
                setNotifications(prev => [notification, ...prev]);
                setLatestNotification(notification);
            });

            // Handle notification marked as read
            connection.on('notificationRead', ({ id }: { id: number }) => {
                setNotifications(prev =>
                    prev.map(n => (n.id === id ? { ...n, unread: false } : n))
                );
            });

            connection.onreconnected(async () => {
                setIsConnected(true);
                await fetchNotifications();
            });

            connection.onclose(() => {
                setIsConnected(false);
                if (!stopped) {
                    setTimeout(startConnection, 3000);
                }
            });

            try {
                await connection.start();
                if (stopped) {
                    await connection.stop();
                    return;
                }

                setIsConnected(true);
                connectionRef.current = connection;
                await fetchNotifications();
            } catch (error) {
                console.error('[Atlas Widget] SignalR connection failed:', error);
                setIsConnected(false);
                if (!stopped) {
                    setTimeout(startConnection, 3000);
                }
            }
        }

        startConnection();

        return () => {
            stopped = true;
            if (connectionRef.current) {
                connectionRef.current.stop().catch(() => { });
                connectionRef.current = null;
            }
        };
    }, [isAuthenticated, token, config.apiUrl, fetchNotifications]);

    const value = useMemo<WidgetNotificationsContextValue>(
        () => ({
            notifications,
            unreadCount,
            isConnected,
            isLoading,
            latestNotification,
            clearLatestNotification,
            markAsRead,
        }),
        [notifications, unreadCount, isConnected, isLoading, latestNotification, clearLatestNotification, markAsRead]
    );

    return (
        <WidgetNotificationsContext.Provider value={value}>
            {children}
        </WidgetNotificationsContext.Provider>
    );
};
